/**
 * DexScanGapReview — b17a.
 *
 * The 3+-conflict surface for bidirectional scan reconciliation. When a live
 * scan confidently reads several cells as NOT caught that the user has manually
 * marked caught, the menu opens this review card instead of a stack of toasts
 * (1-2 conflicts use a toast — see DexLiveScanMenu). Each row is a SUGGESTION:
 * the user confirms "mark as not caught" per-Pokémon or keeps it as caught. We
 * NEVER mutate caught→not-caught without an explicit per-row confirm — surfacing
 * a gap helps the user keep their Dex an accurate mirror, it doesn't overwrite
 * their data behind their back.
 *
 * Confirming a row applies `setManual(variant, dexNumber, "missing")`: the gap
 * the scan found becomes a manual "not caught" mark the user now owns. Keeping a
 * row simply drops it from the list — the manual "caught" mark stands.
 */
import { useState } from "react";
import { Check, X } from "lucide-react";

import { setManual } from "@/lib/dexStateStore";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import type { DexReconciliationConflict } from "@/lib/dexReconciliation";

export interface DexScanGapReviewProps {
  open: boolean;
  conflicts: DexReconciliationConflict[];
  /** Called once the user is done (after resolving or dismissing). `confirmed` is the number marked not-caught. */
  onClose: (confirmed: number) => void;
}

function variantLabel(variant: PokedexVariant): string {
  if (variant === "pokemon") return "Pokémon";
  if (variant === "xxs" || variant === "xxl" || variant === "gmax") {
    return variant.toUpperCase();
  }
  return variant.charAt(0).toUpperCase() + variant.slice(1);
}

function conflictKey(c: DexReconciliationConflict): string {
  return `${c.variant}:${c.dexNumber}`;
}

export function DexScanGapReview({ open, conflicts, onClose }: DexScanGapReviewProps) {
  // Rows the user has resolved this session, keyed `variant:dexNumber`. A
  // resolved row collapses out of the list; we track the count we confirmed so
  // the closing summary can report it.
  const [resolved, setResolved] = useState<Set<string>>(() => new Set());
  const [confirmedCount, setConfirmedCount] = useState(0);

  if (!open || conflicts.length === 0) return null;

  const remaining = conflicts.filter((c) => !resolved.has(conflictKey(c)));

  function markResolved(c: DexReconciliationConflict, didConfirm: boolean) {
    setResolved((prev) => {
      const next = new Set(prev);
      next.add(conflictKey(c));
      return next;
    });
    if (didConfirm) setConfirmedCount((n) => n + 1);
  }

  function handleConfirm(c: DexReconciliationConflict) {
    // The user agrees the scan is right: record a manual "not caught" mark they
    // now own, replacing the manual "caught" the conflict was raised against.
    setManual(c.variant, c.dexNumber, "missing");
    markResolved(c, true);
  }

  function handleKeep(c: DexReconciliationConflict) {
    // The user trusts their own mark over the scan — leave it caught, drop the row.
    markResolved(c, false);
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-label="Review possible Dex gaps"
      data-testid="dex-scan-gap-review"
      onClick={() => onClose(confirmedCount)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-sm p-5 rounded-t-3xl md:rounded-3xl bg-[var(--surface-overlay)] border border-[var(--border-default)] shadow-[var(--shadow-lg)] max-h-[85dvh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-1">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--accent-fill)]">
              POSSIBLE GAPS
            </p>
            <h3 className="text-lg font-extrabold mt-1 text-[var(--text-primary)]">
              {conflicts.length} to review
            </h3>
          </div>
          <button
            onClick={() => onClose(confirmedCount)}
            aria-label="Close"
            data-testid="dex-scan-gap-review-close"
            className="w-8 h-8 rounded-full flex items-center justify-center bg-[var(--surface-sunken)] border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-[11px] mb-4 text-[var(--text-secondary)] leading-relaxed">
          Nexus read these as not caught, but you have them marked caught. Mark
          as not caught, or keep them as caught.
        </p>

        {remaining.length === 0 ? (
          <p
            className="text-sm font-bold text-center py-6 text-[var(--text-primary)]"
            data-testid="dex-scan-gap-review-done"
          >
            All reviewed.
          </p>
        ) : (
          <ul className="flex flex-col gap-2 mb-4" data-testid="dex-scan-gap-review-list">
            {remaining.map((c) => (
              <li
                key={conflictKey(c)}
                className="flex items-center justify-between gap-2 p-2.5 rounded-2xl bg-[var(--surface-sunken)] border border-[var(--border-subtle)]"
                data-testid={`dex-scan-gap-row-${c.variant}-${c.dexNumber}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate text-[var(--text-primary)]">
                    {c.speciesName}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                    #{c.dexNumber} · {variantLabel(c.variant)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleKeep(c)}
                    data-testid={`dex-scan-gap-keep-${c.variant}-${c.dexNumber}`}
                    className="text-[11px] font-bold rounded-xl px-3 py-2 bg-[var(--surface-raised)] border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    Keep
                  </button>
                  <button
                    onClick={() => handleConfirm(c)}
                    data-testid={`dex-scan-gap-confirm-${c.variant}-${c.dexNumber}`}
                    className="text-[11px] font-bold rounded-xl px-3 py-2 inline-flex items-center gap-1 bg-[var(--accent-fill)] text-[var(--text-on-accent)] hover:bg-[var(--accent-hover)] border border-transparent"
                  >
                    <Check className="w-3 h-3" />
                    Not caught
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={() => onClose(confirmedCount)}
          data-testid="dex-scan-gap-review-done-button"
          className="w-full rounded-2xl py-3 text-sm font-bold bg-[var(--surface-sunken)] border border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-[var(--border-default)]"
        >
          Done
        </button>
      </div>
    </div>
  );
}
